/**
 * Usage statistics — how people move through Kai, sent to PostHog.
 *
 * "Statistics" rather than "analytics" on purpose. The Analytics page in Kai
 * (/analytics, the reporting plugin) is a product feature showing customers how
 * their AI engines perform. This file is about how our own users use the portal,
 * and the two are unrelated. The name also matches Cookiebot, whose consent
 * category for this kind of tracking is literally "statistics".
 *
 * Backstage's own API keeps its original names (useAnalytics, analyticsApiRef) —
 * those are external and cannot be renamed. See statisticsApi.ts, which adapts
 * that API to this file.
 */
import posthog from 'posthog-js';

const POSTHOG_PROJECT_KEY = 'phc_g17VJ11p5Jj74xz2JSk65mxp5hVOJ4UQy58HHFb0Q8W';

let initialized = false;
let pendingIdentity: {
  distinctId: string;
  properties: Record<string, unknown>;
} | null = null;

const ENGINEERING_HOST = 'portal.marketplace.deepc-engineering.de';

function isProductionHost(): boolean {
  const parts = window.location.host.split('.');
  const domain = parts.slice(parts.length - 2).join('.');
  return domain === 'deepc.pro';
}

// Engineering is used only by deepc staff and has no Cookiebot, so statistics
// run there without a consent step.
function isEngineeringHost(): boolean {
  return window.location.host === ENGINEERING_HOST;
}

function statisticsAllowed(): boolean {
  if (isEngineeringHost()) return true;
  return Boolean((window as any).Cookiebot?.consent?.statistics);
}

// PostHog draws popover surveys as a floating card with no overlay, so the
// page behind stays usable. Surveys we want answered before anything else
// (the post-submission one, set in PostHog to hide its close button) get a
// backdrop from here: it goes up when a survey is shown and comes down once
// the survey is finished or dismissed. It sits one below the z-index the
// surveys are configured with, so the card itself stays clickable.
const SURVEY_Z_INDEX = 2147482647;
let surveyBackdrop: HTMLDivElement | null = null;

function showSurveyBackdrop() {
  if (surveyBackdrop) return;
  surveyBackdrop = document.createElement('div');
  surveyBackdrop.setAttribute('aria-hidden', 'true');
  Object.assign(surveyBackdrop.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(0, 0, 0, 0.6)',
    zIndex: String(SURVEY_Z_INDEX - 1),
  });
  document.body.appendChild(surveyBackdrop);
}

function hideSurveyBackdrop() {
  surveyBackdrop?.remove();
  surveyBackdrop = null;
}

function trackSurveyBackdrop(event: {
  event?: string;
  properties?: Record<string, unknown>;
}) {
  switch (event.event) {
    case 'survey shown':
      showSurveyBackdrop();
      break;
    case 'survey sent':
      // With partial responses on, every answered question sends one; only
      // the last is marked completed.
      if (event.properties?.$survey_completed !== false) hideSurveyBackdrop();
      break;
    case 'survey dismissed':
    case 'survey abandoned':
      hideSurveyBackdrop();
      break;
    default:
  }
}

function initPostHog() {
  if (initialized) return;
  initialized = true;
  posthog.init(POSTHOG_PROJECT_KEY, {
    api_host: 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    // posthog-js is a bundled import here, so the instance lives in module scope
    // and nothing in the page can reach it. The PostHog toolbar — used to inspect
    // and define events directly on the site — needs it on `window`, so expose it
    // once the library is ready. This only publishes the handle; what we capture
    // is unchanged.
    loaded: ph => {
      (window as any).posthog = ph;
      ph.on('eventCaptured', trackSurveyBackdrop);
    },
    // Backstage is a single-page app: navigation happens through the history API,
    // never a document load. Without this, posthog-js falls back to its legacy
    // default and captures exactly one $pageview per session — the landing URL —
    // so every in-app route change is invisible. 'history_change' captures each one.
    capture_pageview: 'history_change',
    // Already posthog-js's default; stated explicitly so it is visible here. The
    // project setting "Autocapture" in PostHog still wins: if it is off there,
    // /flags returns autocapture_opt_out and no $autocapture events are sent.
    autocapture: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  });
}

function applyConsent() {
  const consent = (window as any).Cookiebot?.consent;
  if (!consent) return;

  if (consent.statistics) {
    initPostHog();
    // Re-enable capturing in case the user previously opted out.
    posthog.opt_in_capturing();
    // The user may have signed in before consent was granted; apply the
    // identity we stashed so we don't lose session stitching.
    if (pendingIdentity) {
      posthog.identify(pendingIdentity.distinctId, pendingIdentity.properties);
    }
  } else if (initialized) {
    // Consent withdrawn: stop capturing and clear PostHog's cookies/state.
    posthog.opt_out_capturing();
    posthog.reset();
  }
}

/**
 * Start PostHog where it is enabled. On engineering it starts straight away.
 *
 * On production, wire PostHog to Cookiebot consent. PostHog sets
 * statistics-category cookies (ph_*), so it must only run once the user has
 * granted statistics consent in Cookiebot. Because posthog-js is bundled into
 * the app rather than loaded as a standalone <script>, Cookiebot's automatic
 * blocking cannot gate it for us, so we gate it manually here. See
 * public/index.html for the Cookiebot loader.
 */
export function setupStatistics() {
  if (isEngineeringHost()) {
    initPostHog();
    return;
  }
  if (!isProductionHost()) return;

  // Cookiebot dispatches these on every page load once the consent state is
  // known, and again whenever the user changes their choice. It may fire before
  // or after this bundle executes, so we both subscribe and check current state.
  window.addEventListener('CookiebotOnConsentReady', applyConsent);
  window.addEventListener('CookiebotOnAccept', applyConsent);
  window.addEventListener('CookiebotOnDecline', applyConsent);
  applyConsent();
}

/**
 * Identify the signed-in user to PostHog so sessions stitch across devices and
 * logins and retention is measured per person rather than per browser.
 *
 * The distinct id is the Backstage userEntityRef, which in this deployment is an
 * opaque UUID (e.g. user:default/855fe642-...), not a name — so it carries no
 * readable PII on its own, and the UUID -> person mapping stays in our own
 * catalog. We deliberately send only is_internal and email_domain, never the
 * raw email: those are all we need to filter deepc staff and see per-hospital
 * usage, and keeping individual PII out of a third-party system is required by
 * data minimisation even though we have consent.
 *
 * Safe to call before consent/init: the identity is stashed and applied once
 * statistics consent is granted.
 */
export function identifyUser(
  distinctId: string,
  properties: Record<string, unknown>,
) {
  pendingIdentity = { distinctId, properties };
  if (initialized && statisticsAllowed()) {
    posthog.identify(distinctId, properties);
  }
}

/**
 * Reset PostHog on sign-out. Without this, the next person on a shared clinical
 * workstation inherits the previous user's identity, which is both bad data and
 * a privacy problem.
 */
export function resetStatistics() {
  pendingIdentity = null;
  if (initialized) posthog.reset();
}

/**
 * Send a custom event.
 *
 * Deliberately silent when statistics are not running — outside production and
 * engineering hosts, before Cookiebot answers, or when statistics consent was
 * declined. Callers are product code and must never have to care whether
 * tracking is active, so this drops the event rather than throwing or queueing
 * it. Events are not worth retrying: a funnel with a few missing steps is fine,
 * a crashed wizard is not.
 *
 * Attributes are sent to a third party, so pass only non-identifying values —
 * step names, counts, opaque ids. Never engine names, vendor names, filenames or
 * anything else that identifies a customer's unreleased product. See identifyUser
 * above for the reasoning.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, string | number | boolean | undefined>,
) {
  if (!initialized || !statisticsAllowed()) return;
  posthog.capture(event, properties);
}
